import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { theme } from "../../../../theme"
import { Button, ButtonOutlined, ButtonVariant } from "../../../atoms/button"
import { Chip, ChipAction } from "../../../atoms/chip"
import { PageSearch } from "../../../atoms/inputs"
import { Heading1, Heading2, TextLink, TextRegular } from "../../../atoms/text"
import { ChipDropdown } from "../../../molecules/chip-dropdown"
import { CallToActionConfig } from "../types/config/CallToActionConfig"
import { DropdownFilterState, FiltersConfig, ToggleFilterState } from "../types/config/FiltersConfig"
import { SearchConfig } from "../types/config/SearchConfig"
import { SelectionConfig } from "../types/config/SelectionConfig"
import { ContextualAction } from "../types/ContextualAction"

export type HeaderSearchConfig = SearchConfig & {
  onSearchChange: (newSearchValue?: string) => void
  searchValue: string
  setSearchValue: (newSearchValue: string) => void
  isSearchFieldExpanded: boolean
  setIsSearchFieldExpanded: (isExpanded: boolean) => void
}

export type HeaderSelectionConfig = SelectionConfig & {
  selectedRowsUniqueIdentifiers: string[]
}

export type HeaderFiltersConfig = FiltersConfig & {
  handleFilterChange: (newInternalFiltersConfig: FiltersConfig) => void
}

export type TableHeaderProps = {
  isFullScreen: boolean
  tableTitle?: string
  headerSelectionConfig?: HeaderSelectionConfig
  headerSearchConfig?: HeaderSearchConfig
  headerFiltersConfig?: HeaderFiltersConfig
  callToActionConfig?: CallToActionConfig
}

export const TableHeader = (props: TableHeaderProps) => {
  const {
    isFullScreen,
    tableTitle,
    headerSelectionConfig,
    headerSearchConfig,
    headerFiltersConfig,
    callToActionConfig,
  } = props

  const [isResetTextDisplayed, setIsResetTextDisplayed] = useState(false)
  const toggleFilters = headerFiltersConfig?.toggleFilters ?? []
  const dropdownFilters = headerFiltersConfig?.dropdownFilters ?? []
  const numberOfFilters = toggleFilters.length + dropdownFilters.length

  const shouldRenderResetFilters = () => {
    if (numberOfFilters <= 1) {
      return false
    }

    const alreadyAtResetState =
      toggleFilters.every((toggleFilter) => {
        return toggleFilter.chipProps.active === (toggleFilter.resetState ?? false)
      }) &&
      dropdownFilters.every((dropdownFilter) => {
        return dropdownFilter.resetState?.key === dropdownFilter.chipDropdownProps.value?.key
      })

    return !alreadyAtResetState
  }

  useEffect(() => {
    setIsResetTextDisplayed(shouldRenderResetFilters())
  }, [dropdownFilters, toggleFilters])

  const renderContextualAction = (contextualAction: ContextualAction) => {
    const { actionCallback, icon, text, dataCy } = contextualAction
    const callback = () => actionCallback(headerSelectionConfig?.selectedRowsUniqueIdentifiers ?? [])

    return (
      <ButtonOutlined data-cy={dataCy} icon={icon} onClick={callback}>
        {text}
      </ButtonOutlined>
    )
  }

  const renderSelectionArea = () => {
    const numberOfSelectedRows = headerSelectionConfig?.selectedRowsUniqueIdentifiers.length ?? 0

    return (
      !!headerSelectionConfig && (
        <>
          <VisibilityManagedSpan $visibility={!!numberOfSelectedRows && !!headerSelectionConfig}>
            {`${numberOfSelectedRows} ${
              !!numberOfSelectedRows && numberOfSelectedRows > 1
                ? headerSelectionConfig?.selectionTextPlural ?? ""
                : headerSelectionConfig?.selectionText ?? ""
            }`}
          </VisibilityManagedSpan>
          {headerSelectionConfig.contextualActions.map((action, index) => {
            return (
              <ContextualArea key={`contextual-header-${index}`}>
                {numberOfSelectedRows > 0 && renderContextualAction(action)}
              </ContextualArea>
            )
          })}
          {numberOfSelectedRows > 0 && headerSelectionConfig.contextualActions.length > 0 && <span>|</span>}
        </>
      )
    )
  }

  const renderSelectionAndActionArea = () => {
    return <SelectAndActionArea>{!!headerSelectionConfig && renderSelectionArea()}</SelectAndActionArea>
  }

  const renderSearch = (asContent: boolean) => {
    if (!headerSearchConfig) return null

    return (
      <PageSearch
        label={headerSearchConfig?.searchLabel}
        placeholder={headerSearchConfig?.searchPlaceholder}
        dynamicWidth={!asContent}
        customSearchBarWidth={asContent ? "464px" : undefined}
        value={headerSearchConfig.searchValue}
        onChange={headerSearchConfig.setSearchValue}
        onClear={() => {
          headerSearchConfig.setSearchValue("")
          headerSearchConfig.onSearchChange("")
        }}
        onSubmit={headerSearchConfig.onSearchChange}
        isExpanded={headerSearchConfig.isSearchFieldExpanded}
        onFocus={() => headerSearchConfig.setIsSearchFieldExpanded(true)}
        isFocused={headerSearchConfig.isSearchFieldExpanded}
        onClearSideEffects={() => headerSearchConfig.setIsSearchFieldExpanded(false)}
        iconWhenShrinked={asContent}
        dataCy={headerSearchConfig.dataCy}
      />
    )
  }

  const headerHandleChangeDropdownFilter = (dropdownFilterState: DropdownFilterState) => {
    if (!headerFiltersConfig) return

    const newDropdownFilters = dropdownFilters.map((dropdownFilter) => {
      if (dropdownFilter.technicalName === dropdownFilterState.technicalName) {
        return {
          technicalName: dropdownFilter.technicalName,
          resetState: dropdownFilter.resetState,
          chipDropdownProps: {
            ...dropdownFilter.chipDropdownProps,
            value: dropdownFilterState.selectedOption,
          },
        }
      }

      return dropdownFilter
    })

    const newInternalFiltersConfig = { ...headerFiltersConfig, dropdownFilters: newDropdownFilters }
    headerFiltersConfig.handleFilterChange(newInternalFiltersConfig)
  }

  const headerHandleChangeToggleFilter = (toggleFilterState: ToggleFilterState) => {
    if (!headerFiltersConfig) return

    const newToggleFilters = toggleFilters.map((toggleFilter) => {
      if (toggleFilter.technicalName === toggleFilterState.technicalName) {
        return {
          technicalName: toggleFilter.technicalName,
          resetState: toggleFilter.resetState,
          chipProps: {
            ...toggleFilter.chipProps,
            active: toggleFilterState.active,
          },
        }
      }

      return toggleFilter
    })

    const newInternalFiltersConfig = { ...headerFiltersConfig, toggleFilters: newToggleFilters }
    headerFiltersConfig.handleFilterChange(newInternalFiltersConfig)
  }

  const handleReset = () => {
    const newToggleFilters = toggleFilters.map((toggleFilter) => {
      return {
        technicalName: toggleFilter.technicalName,
        resetState: toggleFilter.resetState,
        chipProps: {
          ...toggleFilter.chipProps,
          active: toggleFilter.resetState ?? false,
        },
      }
    })

    const newDropdownFilters = dropdownFilters.map((dropdownFilter) => {
      return {
        technicalName: dropdownFilter.technicalName,
        resetState: dropdownFilter.resetState,
        chipDropdownProps: {
          ...dropdownFilter.chipDropdownProps,
          value: dropdownFilter.resetState ?? dropdownFilter.chipDropdownProps.value,
        },
      }
    })

    if (!!headerFiltersConfig) {
      const newInternalFiltersConfig = {
        ...headerFiltersConfig,
        toggleFilters: newToggleFilters,
        dropdownFilters: newDropdownFilters,
      }
      headerFiltersConfig.handleFilterChange(newInternalFiltersConfig)
    }
  }

  const renderToggleFilters = () =>
    toggleFilters.map((toggleFilter, index) => (
      <Chip
        {...toggleFilter.chipProps}
        action={ChipAction.Clickable}
        key={`toggleFilter-${index}`}
        onClick={(e, checked) => {
          checked !== undefined &&
            headerHandleChangeToggleFilter({
              technicalName: toggleFilter.technicalName,
              active: checked,
            })
        }}
      />
    ))

  const renderDropdownFilters = () =>
    dropdownFilters.map((dropdownFilter, index) => (
      <ChipDropdown
        {...dropdownFilter.chipDropdownProps}
        onChange={(value) => {
          return headerHandleChangeDropdownFilter({
            technicalName: dropdownFilter.technicalName,
            selectedOption: value,
          })
        }}
        key={`dropdownFilter-${index}`}
      />
    ))

  const renderFilters = () => {
    if (!headerFiltersConfig || numberOfFilters === 0) return null

    return (
      <>
        <TextRegular> {headerFiltersConfig.filtersText} </TextRegular>

        {renderToggleFilters()}
        {renderDropdownFilters()}
        {isResetTextDisplayed && <TextLink onClick={handleReset}>{headerFiltersConfig.resetText}</TextLink>}
      </>
    )
  }

  const renderFullPageHeader = () => {
    return (
      <>
        <FullPageHeader>
          {!headerSearchConfig?.isSearchFieldExpanded && (
            <>
              {tableTitle && <Heading1>{tableTitle}</Heading1>}
              {renderSelectionAndActionArea()}
            </>
          )}
          {renderSearch(false)}
          {callToActionConfig && !headerSearchConfig?.isSearchFieldExpanded && (
            <CallToActionButton
              variant={ButtonVariant.primary}
              rounded
              icon={callToActionConfig.icon}
              onClick={callToActionConfig.onClick}
              data-cy={callToActionConfig.dataCy}
            >
              {callToActionConfig.label}
            </CallToActionButton>
          )}
        </FullPageHeader>
        {callToActionConfig && !headerSearchConfig?.isSearchFieldExpanded && <VerticalSpacer />}
        {!!headerFiltersConfig ? (
          <Actions $isFullScreen={isFullScreen}>
            <LeftActions>{renderFilters()}</LeftActions>
          </Actions>
        ) : (
          <LXVerticalSpacer />
        )}
      </>
    )
  }

  const renderAsContentPageHeader = () => {
    return (
      <AsContentHeader>
        {/* If there is no other elements in the 2nd bar than the selectAndDownloadArea we put it on the same line as title */}
        {!!headerSearchConfig || !!headerFiltersConfig || !!callToActionConfig ? (
          <>
            <AsContentHeaderWrapper>
              {tableTitle && <Heading2>{tableTitle}</Heading2>}
              <RightActions>
                {renderSelectionAndActionArea()}
                {renderSearch(true)}
                {callToActionConfig && (
                  <ButtonOutlined
                    icon={callToActionConfig.icon}
                    onClick={callToActionConfig.onClick}
                    data-cy={callToActionConfig.dataCy}
                  >
                    {callToActionConfig?.label}
                  </ButtonOutlined>
                )}
              </RightActions>
              {tableTitle && <VerticalSpacer />}
            </AsContentHeaderWrapper>

            <Actions $isFullScreen={isFullScreen}>
              <LeftActions>{renderFilters()}</LeftActions>
            </Actions>
          </>
        ) : (
          <SingleLineDisplay>
            <Heading2>{tableTitle}</Heading2>
            {renderSelectionAndActionArea()}
          </SingleLineDisplay>
        )}
      </AsContentHeader>
    )
  }

  return isFullScreen ? renderFullPageHeader() : renderAsContentPageHeader()
}

const FullPageHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${theme.spacing.lx};
  gap: ${theme.spacing.m};
  height: 104px;

  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 2px 1px rgba(0, 0, 0, 0.12), 0px 1px 3px rgba(0, 0, 0, 0.2);
`

const AsContentHeader = styled.div`
  display: flex;
  flex-direction: column;

  padding: ${theme.spacing.lx} ${theme.spacing.lx} ${theme.spacing.l};
`

const AsContentHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  min-height: 60px;
  margin-bottom: ${theme.spacing.l};
`

const CallToActionButton = styled(Button)`
  position: absolute;
  top: 84px;
  left: 24px;
  box-shadow: 0 0 0 0 rgba(90, 153, 212, 1);
`

const VerticalSpacer = styled.div`
  min-height: ${theme.spacing.l};
`

const LXVerticalSpacer = styled.div`
  min-height: ${theme.spacing.lx};
`

const Actions = styled.div<{ $isFullScreen: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: ${(p) => {
    return p.$isFullScreen ? theme.spacing.lx : 0
  }};

  height: 40px;
`

const LeftActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.spacing.m};
`

const RightActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.spacing.m};

  margin-left: auto;
`

const SingleLineDisplay = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const SelectAndActionArea = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.m};
  margin-left: auto;
  align-items: center;
`

const ContextualArea = styled.div`
  svg {
    fill: ${theme.color.blue2};
  }
`

// Using visibility instead of conditional rendering remove all risks of redimensioning upon action
const VisibilityManagedSpan = styled.span<{ $visibility: boolean }>`
  visibility: ${(p) => {
    return p.$visibility ? "unset" : "hidden"
  }};
`
